import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SocialMediaService } from '../social-media.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

export interface SocialMediaForm {
  social_media_id?: number;
  social_media_platform: string;
  social_media_url: string;
  social_media_order: number;
  social_media_status: 'ACTI' | 'INAC';
}

@Component({
  selector: 'app-social-media-form',
  standalone: false,
  templateUrl: './social-media-form.component.html',
  styleUrls: ['./social-media-form.component.scss']
})
export class SocialMediaFormComponent implements OnInit, OnDestroy {
  itemId = signal<number | null>(null);
  isLoading = signal(false);
  isFetching = signal(false);
  errorMessage = signal<string | null>(null);

  formData = signal<SocialMediaForm>({
    social_media_platform: '',
    social_media_url: '',
    social_media_order: 0,
    social_media_status: 'INAC'
  });

  platforms = ['Facebook', 'Instagram', 'X (Twitter)', 'YouTube', 'TikTok', 'LinkedIn', 'Autre'];

  private destroy$ = new Subject<void>();

  constructor(
    private socialMediaService: SocialMediaService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.itemId.set(Number(idParam));
      this.loadItem(Number(idParam));
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadItem(id: number): void {
    this.isFetching.set(true);
    this.errorMessage.set(null);

    this.socialMediaService.getSocialMediaById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const m = response.data || response;
          this.formData.set({
            social_media_id: m.social_media_id,
            social_media_platform: m.social_media_platform || '',
            social_media_url: m.social_media_url || '',
            social_media_order: m.social_media_order || 0,
            social_media_status: m.social_media_status || 'INAC'
          });
          this.isFetching.set(false);
        },
        error: (err) => {
          this.isFetching.set(false);
          SwalUtils.error('Erreur', err?.error?.message || 'Impossible de charger ce réseau social.');
        }
      });
  }

  async onSave(): Promise<void> {
    const data = this.formData();
    if (!data.social_media_platform.trim()) { this.errorMessage.set('La plateforme est requise'); return; }
    if (!data.social_media_url.trim()) { this.errorMessage.set('L\'URL est requise'); return; }

    const confirmed = await SwalUtils.confirmGreen(
      this.isEditMode ? 'Modifier le réseau social' : 'Créer le réseau social',
      this.isEditMode
        ? 'Voulez-vous enregistrer les modifications ?'
        : 'Voulez-vous créer ce réseau social ?',
      this.isEditMode ? 'Modifier' : 'Créer'
    );
    if (!confirmed.isConfirmed) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const payload = {
      social_media_platform: data.social_media_platform,
      social_media_url: data.social_media_url,
      social_media_order: data.social_media_order,
      social_media_status: data.social_media_status,
    };

    const isUpdate = this.itemId() !== null;
    const req$ = isUpdate
      ? this.socialMediaService.updateSocialMedia(this.itemId()!, payload)
      : this.socialMediaService.createSocialMedia(payload);

    req$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.isLoading.set(false);
        SwalUtils.success(
          isUpdate ? 'Réseau social modifié' : 'Réseau social créé',
          isUpdate ? 'Les modifications ont été enregistrées.' : 'Le réseau social a été créé avec succès.'
        ).then(() => {
          this.router.navigate(['/federation/reseaux-sociaux']);
        });
      },
      error: (err) => {
        this.isLoading.set(false);
        SwalUtils.error(
          'Erreur',
          err?.error?.message || 'Une erreur est survenue lors de l\'enregistrement.'
        );
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/federation/reseaux-sociaux']);
  }

  updateField(field: keyof SocialMediaForm, value: any): void {
    this.formData.set({ ...this.formData(), [field]: value });
  }

  get isEditMode(): boolean { return this.itemId() !== null; }
  get pageTitle(): string { return this.isEditMode ? 'Modifier le réseau social' : 'Ajouter un réseau social'; }
}
